import toast from 'react-hot-toast';
import { FACEBOOK, INSTAGRAM, PLATFORM_LIST } from '../../../config/constants';
import { POST_TYPE_KEY, platformFeatures } from './platformFeatures';

import { updatePlatformEnabled } from '../../../redux/slices/lomavisCreator/lomavisCreator';
import { PDF, VIDEO } from './constants';
import {
  getNumberOfAudioChannels,
  getPdfPageCount,
  getVideoDuration,
  readFileAsArrayBuffer
} from '../../../redux/slices/lomavisCreator/utils';
import { lomavisCreatorRootState } from '../../../pages/lomavisCreator';

export const validateUpload = async (
  platform,
  typeKey,
  files,
  t,
  showToasts = true
) => {
  try {
    const postTypeRule = platformFeatures[platform][POST_TYPE_KEY].find(
      (pt) => pt.typeKey === typeKey
    );

    // Check if post type exists or is enabled in platform features
    if (!postTypeRule || !postTypeRule.enabled) {
      showToasts &&
        toast.error(`${t('lomavisCreator.errors.upload_type')}`, {
          duration: 4000,
          position: 'top-right'
        });
      return false;
    }

    const mediaTypesInFiles = new Set();

    for (const file of files) {
      const fileType =
        file.type.split('/')[0] === 'application'
          ? file.type.split('/')[1]
          : file.type.split('/')[0]; // 'video', 'image', etc.

      // Check if media type is allowed for file type
      if (
        !postTypeRule.mediaTypes[fileType] ||
        !postTypeRule.mediaTypes[fileType].enabled
      ) {
        showToasts &&
          toast.error(`${t('lomavisCreator.errors.file_not_supported')}`, {
            duration: 4000,
            position: 'top-right'
          });
        return false;
      }

      // Check PDF page number
      if (fileType === PDF) {
        let pageCount = null;
        if (file?.pdf_page_count) {
          pageCount = file?.pdf_page_count;
        } else {
          pageCount = await getPdfPageCount(file);
        }
        const maxPageCount = postTypeRule.mediaTypes[fileType]?.maxPageNumbers;

        if (pageCount && pageCount > maxPageCount) {
          showToasts &&
            toast.error(
              `${t(
                'lomavisCreator.errors.exceeded_max_page_num'
              )} ${maxPageCount}`,
              {
                duration: 4000,
                position: 'top-right'
              }
            );
          return false;
        }
      }
      // check video file duration
      if (
        (postTypeRule?.mediaTypes[fileType] ||
          postTypeRule?.mediaTypes[fileType]?.enabled) &&
        fileType === VIDEO
      ) {
        if (
          postTypeRule?.mediaTypes[VIDEO]?.minDurationInSeconds &&
          postTypeRule?.mediaTypes[VIDEO]?.maxDurationInSeconds
        ) {
          let video_duration;
          try {
            video_duration = (await getVideoDuration(file)) / 1000;
          } catch (error) {
            console.error('Failed to get video duration:', error);
          }
          if (
            (video_duration &&
              video_duration <
                postTypeRule?.mediaTypes[VIDEO]?.minDurationInSeconds) ||
            video_duration >
              postTypeRule?.mediaTypes[VIDEO]?.maxDurationInSeconds
          ) {
            showToasts &&
              toast.error(
                `${t(`platforms.${platform}`)}: ${t(
                  'invalid.duration.error.message'
                )}
                  ${t('lomavisCreator.allowed.time')}: ${
                  postTypeRule?.mediaTypes[VIDEO]?.minDurationInSeconds
                }s - ${postTypeRule?.mediaTypes[VIDEO]?.maxDurationInSeconds}s.
                  ${t('lomavisCreator.current.time')}: ${
                  Math.round(video_duration * 10) / 10
                }s`,
                {
                  duration: 4000,
                  position: 'top-right'
                }
              );
            return false;
          }
        }
      }

      mediaTypesInFiles.add(fileType);

      const maxFiles = postTypeRule.mediaTypes[fileType]?.maxFiles;
      const uploadedFilesCount = files.filter((f) => {
        const fType =
          f.type.split('/')[0] === 'application'
            ? f.type.split('/')[1]
            : f.type.split('/')[0];
        return fType === fileType;
      }).length;

      // Max files per media type
      if (uploadedFilesCount > maxFiles) {
        showToasts &&
          toast.error(
            `${t('lomavisCreator.errors.exceeded_max_file_num')} ${maxFiles}`,
            {
              duration: 4000,
              position: 'top-right'
            }
          );
        return false;
      }

      // Max file size in bytes
      const maxFileSize = postTypeRule.mediaTypes[fileType]?.maxFileSizeInBytes;

      if (maxFileSize && file.size > maxFileSize) {
        showToasts &&
          toast.error(`${t('lomavisCreator.errors.exceeded_max_file_size')}`, {
            duration: 4000,
            position: 'top-right'
          });
        return false;
      }

      // Check number of audio channels
      const maxAudioChannels =
        postTypeRule.mediaTypes[fileType]?.maxAudioChannels;

      if (maxAudioChannels) {
        let numberOfChannels = null;

        if ('video_audio_channels' in file) {
          numberOfChannels = file?.video_audio_channels;
        } else {
          const fileBuffer = await readFileAsArrayBuffer(file);
          numberOfChannels = await getNumberOfAudioChannels(fileBuffer);
        }

        if (numberOfChannels && numberOfChannels > maxAudioChannels) {
          showToasts &&
            toast.error(
              `${t('lomavisCreator.errors.exceeded_max_audio_channels')}`,
              {
                duration: 4000,
                position: 'top-right'
              }
            );
          return false;
        }
      }

      // Check if mixed files are allowed
      if (
        mediaTypesInFiles.size > 1 &&
        !postTypeRule.allowMixedMediaMultiFile
      ) {
        showToasts &&
          toast.error(`${t('lomavisCreator.errors.mixed_files_not_allowed')}`, {
            duration: 4000,
            position: 'top-right'
          });
        return false;
      }

      if (platform === INSTAGRAM) {
        if (files?.length > 1) {
          if (fileType === VIDEO) {
            let video_duration;
            try {
              video_duration = await getVideoDuration(file);
              console.log('Video duration:', video_duration, 'ms');
            } catch (error) {
              console.error('Failed to get video duration:', error);
            }
            if (video_duration && video_duration > 60000) {
              showToasts &&
                toast.error(
                  `${t(`platforms.${platform}`)}: ${t(
                    'IG.duration.error.message'
                  )}`,
                  {
                    duration: 4000,
                    position: 'top-right'
                  }
                );
              return false;
            }
          }
        }
      }
    }

    // If all checks pass
    return true;
  } catch (error) {
    console.error('Validation error:', error);
    return false;
  }
};

export const validateAllUpload = async (
  platform,
  typeKeyAll,
  files,
  t,
  dispatch,
  platformEnabled,
  forConfirmation = false
) => {
  const allEnabledPlatforms = PLATFORM_LIST.filter(
    (item) => platformEnabled[item] === true
  );

  const checkValidation = async (simulate) => {
    let isConfirmed = true;
    const newEnabledPlatform = [...allEnabledPlatforms];
    const updateEnablePlatform = (platform) => {
      const index = newEnabledPlatform.indexOf(platform);
      if (index > -1) {
        newEnabledPlatform.splice(index, 1); // This mutates the original array
      }
    };

    let allowedMedia = 0;
    let error_message;
    const platform_error_message = t('lomavisCreator.platform.disabled');
    let currentPlatform;
    for (const platform of allEnabledPlatforms) {
      currentPlatform = platform;
      const postTypeRule = platformFeatures[platform]?.[POST_TYPE_KEY].find(
        (pt) => pt.typeKey === typeKeyAll[platform]
      );

      let filesAreValid = true;
      const mediaTypesInFiles = new Set();
      for (const file of files) {
        const fileType =
          file?.type.split('/')[0] === 'application'
            ? file.type.split('/')[1]
            : file.type.split('/')[0]; // 'video', 'image', etc.

        // Check if media type is allowed for file type
        if (
          !postTypeRule?.mediaTypes[fileType] ||
          !postTypeRule?.mediaTypes[fileType].enabled
        ) {
          filesAreValid = false;

          error_message = t('lomavisCreator.error.invalid_file_type');
        } else if (
          // check pdf page number
          (postTypeRule?.mediaTypes[fileType] ||
            postTypeRule?.mediaTypes[fileType].enabled) &&
          fileType === PDF
        ) {
          try {
            const pageCount = getPdfPageCount(file);

            const maxPageCount =
              postTypeRule?.mediaTypes[fileType]?.maxPageNumbers;

            if (pageCount && pageCount > maxPageCount) {
              filesAreValid = false;
              error_message = `${t(
                'lomavisCreator.errors.exceeded_max_page_num'
              )}${maxPageCount}`;
              break; // Exit the loop early if a file is not valid
            }
          } catch (error) {
            console.error('Error getting PDF page count:', error);
          }
        }
        // check if post type exist or is enabled in plaform features
        if (!postTypeRule || !postTypeRule.enabled) {
          filesAreValid = false;
          error_message = t('lomavisCreator.errors.upload_type');
          break;
        }

        // max files per media type
        mediaTypesInFiles.add(fileType);

        const maxFiles = postTypeRule?.mediaTypes[fileType]?.maxFiles;
        const uploadedFilesCount = files.filter((f) =>
          file.type.split('/')[0] === 'application'
            ? file.type.split('/')[1]
            : file.type.split('/')[0] === fileType
        ).length;

        if (uploadedFilesCount > maxFiles) {
          filesAreValid = false;
          error_message = `${t(
            'lomavisCreator.errors.exceeded_max_file_num'
          )}${maxFiles}`;
          break;
        }

        // max file size in bytes
        const maxFileSize =
          postTypeRule?.mediaTypes[fileType]?.maxFileSizeInBytes;

        if (maxFileSize && file.size > maxFileSize) {
          filesAreValid = false;
          error_message = `${t(
            'lomavisCreator.errors.exceeded_max_file_size'
          )}`;
          break;
        }

        // check number of audio channels
        // const maxAudioChannels =
        //   postTypeRule.mediaTypes[fileType]?.maxAudioChannels;

        // if (maxAudioChannels) {
        //   let numberOfChannels = null;
        //   if ("video_audio_channels" in file) {
        //     numberOfChannels = file?.video_audio_channels;
        //   } else {
        //     const fileBuffer = await readFileAsArrayBuffer(file);
        //     numberOfChannels = await getNumberOfAudioChannels(fileBuffer);
        //   }

        //   if (numberOfChannels && numberOfChannels > maxAudioChannels) {
        //     filesAreValid = false;
        //     error_message = `${t(
        //       "lomavisCreator.errors.exceeded_max_audio_channels"
        //     )}`;
        //     break;
        //   }
        // }

        // allowing mixed files
        if (
          mediaTypesInFiles.size > 1 &&
          !postTypeRule.allowMixedMediaMultiFile
        ) {
          filesAreValid = false;
          error_message = `${t(
            'llomavisCreator.errors.mixed_files_not_allowed'
          )}`;
          break;
        }
      }

      if (filesAreValid) {
        allowedMedia += 1;
      }
    }

    if (allowedMedia < 1) {
      if (!simulate) {
        toast.error(
          `${t(`platforms.${currentPlatform}`)}: ${t(platform_error_message)}`,
          {
            duration: 4000,
            position: 'top-right',
            id: currentPlatform
          }
        );
      }
      toast.error(` ${t('lomavisCreator.noPlatform.error.message')}`, {
        duration: 4000,
        position: 'top-right'
      });

      return false;
    }
    if (forConfirmation) {
      if (error_message) {
        isConfirmed = false;
      }
    }

    for (const platform of allEnabledPlatforms) {
      if (!platformEnabled[platform]) {
        return;
      }
      const postTypeRule = platformFeatures[platform]?.[POST_TYPE_KEY].find(
        (pt) => pt.typeKey === typeKeyAll[platform]
      );

      // check if post type exist or is enabled in plaform features
      if (!postTypeRule || !postTypeRule.enabled) {
        updateEnablePlatform(platform);
        if (!simulate) {
          dispatch(
            updatePlatformEnabled({
              platform,
              enabled: false
            })
          );
          toast.error(
            `${t(`platforms.${platform}`)}: ${t(platform_error_message)}`,
            {
              duration: 4000,
              position: 'top-right',
              id: platform
            }
          );
        }
      }

      const mediaTypesInFiles = new Set();
      for (const file of files) {
        const fileType =
          file.type.split('/')[0] === 'application'
            ? file.type.split('/')[1]
            : file.type.split('/')[0]; // 'video', 'image', etc.
        // check if media type is allowed for file type
        if (
          !postTypeRule?.mediaTypes[fileType] ||
          !postTypeRule?.mediaTypes[fileType]?.enabled
        ) {
          if (forConfirmation) {
            isConfirmed = false;
          }
          if (!simulate) {
            dispatch(
              updatePlatformEnabled({
                platform,
                enabled: false
              })
            );
            toast.error(
              `${t(`platforms.${platform}`)}: ${t(platform_error_message)}`,
              {
                duration: 4000,
                position: 'top-right',
                id: platform
              }
            );
          }

          updateEnablePlatform(platform);
        } else if (
          // check pdf page number
          (postTypeRule?.mediaTypes[fileType] ||
            postTypeRule?.mediaTypes[fileType]?.enabled) &&
          fileType === PDF
        ) {
          try {
            let pageCount = null;
            if (file?.pdf_page_count) {
              pageCount = file?.pdf_page_count;
            } else {
              pageCount = await getPdfPageCount(file);
            }

            const maxPageCount =
              postTypeRule?.mediaTypes[fileType]?.maxPageNumbers;

            if (pageCount && pageCount > maxPageCount) {
              if (forConfirmation) {
                isConfirmed = false;
              }
              if (!simulate) {
                dispatch(
                  updatePlatformEnabled({
                    platform,
                    enabled: false
                  })
                );
                toast.error(
                  `${t(`platforms.${platform}`)}: ${t(platform_error_message)}`,
                  {
                    duration: 4000,
                    position: 'top-right',
                    id: platform
                  }
                );
              }

              updateEnablePlatform(platform);
            }
          } catch (error) {
            console.error('Error getting PDF page count:', error);
          }
        } else if (
          (postTypeRule?.mediaTypes[fileType] ||
            postTypeRule?.mediaTypes[fileType]?.enabled) &&
          fileType === VIDEO
        ) {
          if (
            postTypeRule?.mediaTypes[VIDEO]?.minDurationInSeconds &&
            postTypeRule?.mediaTypes[VIDEO]?.maxDurationInSeconds
          ) {
            let video_duration;
            try {
              video_duration = (await getVideoDuration(file)) / 1000;
            } catch (error) {
              console.error('Failed to get video duration:', error);
            }
            if (
              (video_duration &&
                video_duration <
                  postTypeRule?.mediaTypes[VIDEO]?.minDurationInSeconds) ||
              video_duration >
                postTypeRule?.mediaTypes[VIDEO]?.maxDurationInSeconds
            ) {
              if (forConfirmation) {
                isConfirmed = false;
              }
              if (!simulate) {
                dispatch(
                  updatePlatformEnabled({
                    platform,
                    enabled: false
                  })
                );
                toast.error(
                  `${t(`platforms.${platform}`)}: ${t(
                    'invalid.duration.error.message'
                  )}
                  ${t('lomavisCreator.allowed.time')}: ${
                    postTypeRule?.mediaTypes[VIDEO]?.minDurationInSeconds
                  }s - ${
                    postTypeRule?.mediaTypes[VIDEO]?.maxDurationInSeconds
                  }s.
                  ${t('lomavisCreator.current.time')}: ${Math.ceil(
                    video_duration
                  )}s`,
                  {
                    duration: 4000,
                    position: 'top-right'
                  }
                );
                toast.error(
                  `${t(`platforms.${platform}`)}: ${t(platform_error_message)}`,
                  {
                    duration: 4000,
                    position: 'top-right',
                    id: platform
                  }
                );
              }

              updateEnablePlatform(platform);
            }
          }
        }
        mediaTypesInFiles.add(fileType);

        const maxFiles = postTypeRule?.mediaTypes[fileType]?.maxFiles;

        const uploadedFilesCount = files.filter((f) =>
          file.type.split('/')[0] === 'application'
            ? file.type.split('/')[1]
            : file.type.split('/')[0] === fileType
        ).length;
        // max files per media type
        if (uploadedFilesCount > maxFiles) {
          if (forConfirmation) {
            isConfirmed = false;
          }
          if (!simulate) {
            dispatch(
              updatePlatformEnabled({
                platform,
                enabled: false
              })
            );
            toast.error(
              `${t(`platforms.${platform}`)}: ${t(platform_error_message)}`,
              {
                duration: 4000,
                position: 'top-right',
                id: platform
              }
            );
          }

          updateEnablePlatform(platform);
        }

        // max file size in bytes
        const maxFileSize =
          postTypeRule?.mediaTypes[fileType]?.maxFileSizeInBytes;

        if (maxFileSize && file.size > maxFileSize) {
          if (forConfirmation) {
            isConfirmed = false;
          }
          if (!simulate) {
            dispatch(
              updatePlatformEnabled({
                platform,
                enabled: false
              })
            );
            toast.error(
              `${t(`platforms.${platform}`)}: ${t(platform_error_message)}`,
              {
                duration: 4000,
                position: 'top-right',
                id: platform
              }
            );
          }

          updateEnablePlatform(platform);
        }

        // allowing mixed files
        if (
          mediaTypesInFiles.size > 1 &&
          !postTypeRule?.allowMixedMediaMultiFile
        ) {
          if (forConfirmation) {
            isConfirmed = false;
          }
          if (!simulate) {
            dispatch(
              updatePlatformEnabled({
                platform,
                enabled: false
              })
            );
            toast.error(
              `${t(`platforms.${platform}`)}: ${t(platform_error_message)}`,
              {
                duration: 4000,
                position: 'top-right',
                id: platform
              }
            );
          }

          updateEnablePlatform(platform);
        }

        // check instagram video duration
        if (platform === INSTAGRAM) {
          if (files?.length > 1) {
            if (fileType === VIDEO) {
              let video_duration;
              try {
                video_duration = await getVideoDuration(file);
              } catch (error) {
                console.error('Failed to get video duration:', error);
              }
              if (video_duration && video_duration > 60000) {
                updateEnablePlatform(platform);
                if (!simulate) {
                  dispatch(
                    updatePlatformEnabled({
                      platform,
                      enabled: false
                    })
                  );

                  toast.error(
                    `${t(`platforms.${platform}`)}: ${t(
                      platform_error_message
                    )}`,
                    {
                      duration: 4000,
                      position: 'top-right',
                      id: platform
                    }
                  );
                }
                toast.error(
                  `${t(`platforms.${platform}`)}: ${t(
                    'IG.duration.error.message'
                  )}`,
                  {
                    duration: 4000,
                    position: 'top-right'
                  }
                );
              }
            }
          }
        }
      }
    }
    console.log('available platforms', newEnabledPlatform);
    if (newEnabledPlatform?.length < 1) {
      toast.error(` ${t('lomavisCreator.noPlatform.error.message')}`, {
        duration: 4000,
        position: 'top-right'
      });

      return false;
    }
    if (!forConfirmation) {
      return true;
    } else {
      return isConfirmed;
    }
  };

  if (await checkValidation(true)) {
    return await checkValidation(false);
  } else {
    return false;
  }
};
